import { useFrame, useThree } from "@react-three/fiber";
import { useEffect, useRef } from "react";

function diagnosticsEnabled(): boolean {
	if (typeof window === "undefined") return false;
	try {
		return (
			window.localStorage.getItem("bb-viewer-debug-gpu") === "1" ||
			(import.meta.env.DEV &&
				new URLSearchParams(window.location.search).get("viewerDiagnostics") ===
					"1")
		);
	} catch {
		return false;
	}
}

/** Opt-in whole-frame counters. Never requests a frame or changes visual quality. */
export function ViewerRendererDiagnostics() {
	const gl = useThree((state) => state.gl);
	const enabled = useRef(diagnosticsEnabled());
	const samples = useRef<{
		last: number;
		started: number;
		frames: number;
		idleGaps: number;
		deltas: number[];
		calls: number[];
		triangles: number[];
		emptyFrameRun: number;
		maxEmptyFrameRun: number;
	}>({
		last: 0,
		started: 0,
		frames: 0,
		idleGaps: 0,
		deltas: [],
		calls: [],
		triangles: [],
		emptyFrameRun: 0,
		maxEmptyFrameRun: 0,
	});

	useEffect(() => {
		if (!enabled.current) return;
		const autoReset = gl.info.autoReset;
		// Aggregate all scene/shadow/postprocessing renders, not just the last pass.
		// eslint-disable-next-line react-hooks/immutability -- Three renderer counters are mutable; restored below.
		gl.info.autoReset = false;
		gl.info.reset();
		return () => {
			gl.info.autoReset = autoReset;
			gl.info.reset();
			gl.domElement.removeAttribute("data-viewer-gpu");
		};
	}, [gl]);

	useFrame(() => {
		if (!enabled.current) return;
		// Readiness itself reads the previous render counters. Do not clear
		// those until it has completed, or diagnostics would pin the loader.
		if (gl.domElement.getAttribute("data-viewer-frame-ready") !== "1") return;
		const now = performance.now();
		const s = samples.current;
		if (!s.started) s.started = now;
		const delta = s.last ? now - s.last : 0;
		s.last = now;
		s.frames++;
		// Include every post-ready frame, even shader warm-up and demand wake-up.
		s.emptyFrameRun = gl.info.render.calls === 0 ? s.emptyFrameRun + 1 : 0;
		s.maxEmptyFrameRun = Math.max(s.maxEmptyFrameRun, s.emptyFrameRun);
		if (delta > 250) s.idleGaps++;
		// Initial texture/shader warm-up and demand-loop sleep are not steady interaction.
		if (now - s.started > 2500 && delta > 0 && delta <= 250) {
			s.deltas.push(delta);
			s.calls.push(gl.info.render.calls);
			s.triangles.push(gl.info.render.triangles);
			if (s.deltas.length > 240) {
				s.deltas.shift();
				s.calls.shift();
				s.triangles.shift();
			}
		}
		if (s.frames % 10 === 0 || delta > 250) {
			const percentile = (values: number[], p: number) =>
				values.length
					? [...values].sort((a, b) => a - b)[Math.ceil(values.length * p) - 1]
					: null;
			gl.domElement.setAttribute("data-viewer-gpu", JSON.stringify({
				frames: s.frames,
				maxEmptyFrameRun: s.maxEmptyFrameRun,
				samples: s.deltas.length,
				idleGaps: s.idleGaps,
				frameMsP50: percentile(s.deltas, 0.5),
				frameMsP95: percentile(s.deltas, 0.95),
				callsP50: percentile(s.calls, 0.5),
				callsP95: percentile(s.calls, 0.95),
				trianglesP95: percentile(s.triangles, 0.95),
				geometries: gl.info.memory.geometries,
				textures: gl.info.memory.textures,
				programs: gl.info.programs?.length ?? 0,
				canvas: [gl.domElement.width, gl.domElement.height],
			}));
		}
		gl.info.reset();
	}, -100);
	return null;
}
